import React from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import editFill from '@iconify/icons-eva/edit-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import creditCardFill from '@iconify/icons-eva/credit-card-fill';
import flashFill from '@iconify/icons-eva/flash-fill';
import timeFill from '@iconify/icons-eva/clock-fill';

// constants
import { statusColorMap, Status } from '../constants/ocpi.constant';
import { OCPI_ROLES } from 'src/utils/ocpi.constants';

const StyledCard = styled(Card)(({ theme, selected, isValid }) => ({
  border: `2px solid ${selected ? theme.palette.success.main : 'transparent'}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: selected ? theme.shadows[8] : theme.shadows[2],
  transition: 'all 0.3s ease',
  background: selected ? 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)' : 'white',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)'
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const TariffIcon = ({ tariffType }) => {
  const getIcon = () => {
    switch (tariffType) {
      case 'REGULAR':
        return creditCardFill;
      case 'FAST':
        return flashFill;
      case 'GREEN':
        return flashFill;
      case 'CHEAP':
        return creditCardFill;
      case 'PREMIUM':
        return creditCardFill;
      case 'OFF_PEAK':
        return timeFill;
      case 'PEAK':
        return flashFill;
      default:
        return creditCardFill;
    }
  };

  return <Icon icon={getIcon()} width={20} height={20} />;
};

const TariffCard = ({ tariff, selected, onSelect, onView, onEdit, onDelete }) => {
  // Get main pricing component
  const mainPricing = tariff.elements?.[0]?.price_components?.[0] || {};
  const priceType = mainPricing.type || 'ENERGY';
  const price = mainPricing.price || 0;
  const tariffStatus = tariff.active ? Status.VALID : Status.INVALID;

  return (
    <StyledCard selected={selected} isValid={tariff.isValid}>
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with Checkbox and Status */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Checkbox
              checked={selected}
              onChange={() => onSelect(tariff.id)}
              color="success"
              size="small"
            />
            <Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TariffIcon tariffType={tariff.tariffType} />
                <Tooltip title={tariff.id} placement="top">
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: '1rem', 
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '150px'
                    }}
                  >
                    {tariff.id}
                  </Typography>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {new Date(tariff.lastUpdated).toLocaleDateString()}
              </Typography>
            </Stack>
          </Stack>
          <Chip
            label={tariffStatus}
            color={statusColorMap[tariffStatus]?.color || 'default'}
            size="small"
            variant="filled"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              bgcolor: statusColorMap[tariffStatus]?.bgcolor || 'grey.200',
              color: statusColorMap[tariffStatus]?.text || 'text.secondary',
            }}
          />
        </Stack>

        {/* Main Content Grid */}
        <Box sx={{ mb: 2, flex: 1 }}>
          <Stack direction="row" spacing={3}>
            {/* Left Column */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  COUNTRY / PARTY
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {tariff.countryCode} - {tariff.partyId}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  TYPE
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={tariff.tariffType}
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  CURRENCY
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {tariff.currency}
                </Typography>
              </Box>
            </Box>

            {/* Right Column */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  ENERGY CATEGORY
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {tariff.energyMix}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  PRICING
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={`${price} ${tariff.currency}/${priceType === 'ENERGY' ? 'kWh' : 'hour'}`}
                    variant="outlined"
                    size="small"
                    color="secondary"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon={eyeFill} width={16} height={16} />}
            onClick={() => onView(tariff.id)}
            sx={{
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              height: 32,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { 
                borderColor: 'grey.400',
                backgroundColor: 'grey.50'
              }
            }}
          >
            View
          </Button>
          {tariff.role !== OCPI_ROLES.CPO && 
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon={editFill} width={16} height={16} />}
            onClick={() => onEdit(tariff.id)}
            sx={{
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              height: 32,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { 
                borderColor: 'grey.400',
                backgroundColor: 'grey.50'
              }
            }}
          >
            Edit
          </Button>}
          {tariff.role !== OCPI_ROLES.CPO && 
          <IconButton
            size="small"
            onClick={() => onDelete(tariff.id)}
            sx={{
              width: 32,
              height: 32,
              color: 'error.main',
              '&:hover': { 
                backgroundColor: 'error.lighter'
              }
            }}
          >
            <Icon icon={trash2Fill} width={16} height={16} />
          </IconButton>}
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

TariffCard.propTypes = {
  tariff: PropTypes.shape({
    id: PropTypes.string,
    tariff_id: PropTypes.string,
    type: PropTypes.string,
    tariff_type: PropTypes.string,
    country_code: PropTypes.string,
    party_id: PropTypes.string,
    partner_country_code: PropTypes.string,
    partner_party_id: PropTypes.string,
    currency: PropTypes.string,
    energy_category: PropTypes.string,
    restrictions: PropTypes.string,
    valid: PropTypes.bool,
    last_updated: PropTypes.string,
    updated_at: PropTypes.string,
    description: PropTypes.string,
    min_price: PropTypes.number,
    max_price: PropTypes.number,
    elements: PropTypes.array
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default TariffCard;
